import { Link } from "wouter";
import { ArrowLeft, Compass } from "lucide-react";
import SalesFooter from "@/components/marketing/SalesFooter";
import SalesReceptionist from "@/components/marketing/SalesReceptionist";
import { BRAND } from "@/lib/themes";

const BG = "#FFFFFF";
const INK = BRAND.charcoal;
const MUTED = "#555555";
const SAGE = BRAND.sage;
const SAGE_DARK = BRAND.sageDeep;
const BORDER = "#E5E5E5";

export default function NotFound() {
  return (
    <div
      className="marketing-root min-h-screen w-full flex flex-col overflow-x-hidden"
      data-testid="page-not-found"
      style={{
        backgroundColor: BG,
        color: INK,
        fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
            @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap');
          `,
        }}
      />


      <main className="flex-1 flex items-center justify-center px-5 md:px-8 pt-20 pb-20 relative">
        {/* Soft sage halo backdrop */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 50% 0%, rgba(122,139,111,0.10) 0%, transparent 55%)",
          }}
        />

        <div className="relative w-full max-w-2xl text-center">
          {/* EYEBROW */}
          <div className="inline-flex items-center gap-2.5 mb-6">
            <span
              className="inline-flex items-center justify-center w-8 h-8 rounded-full"
              style={{ backgroundColor: `${SAGE}22` }}
            >
              <Compass
                className="w-4 h-4"
                strokeWidth={2.5}
                style={{ color: SAGE_DARK }}
              />
            </span>
            <p
              className="uppercase font-bold"
              style={{
                color: SAGE,
                fontSize: "11px",
                letterSpacing: "0.24em",
              }}
              data-testid="badge-error-code"
            >
              Error 404
            </p>
          </div>

          {/* BIG 404 - DM Sans, soft sage gradient */}
          <div
            className="select-none font-bold tracking-tight mb-2"
            style={{
              fontSize: "clamp(7rem, 22vw, 14rem)",
              lineHeight: 0.92,
              background: `linear-gradient(135deg, ${BRAND.charcoal} 0%, ${BRAND.nearBlack} 45%, ${BRAND.sage} 100%)`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
            data-testid="text-404"
            aria-hidden
          >
            404
          </div>

          {/* HEADLINE */}
          <h1
            className="mb-5 tracking-tight font-bold"
            style={{
              color: INK,
              fontSize: "clamp(2rem, 5vw, 2.875rem)",
              lineHeight: 1.05,
            }}
            data-testid="heading-not-found"
          >
            This page doesn&rsquo;t exist.
          </h1>

          {/* SUBLINE */}
          <p
            className="mx-auto max-w-lg mb-10 leading-relaxed"
            style={{ color: MUTED, fontSize: "17px" }}
            data-testid="text-not-found-subline"
          >
            The link you followed is broken, or the page has moved. Let&rsquo;s
            get you back on track.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-10">
            {/* Plain <a> on purpose: "/" is served by the SERVER (owner's public
                website). A client-side Link would render the SPA's bare fallback
                instead of the real sales page. */}
            <a
              href="/"
              data-testid="link-home"
              className="group inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-[18px] font-bold text-[17px] text-white transition-all hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: `linear-gradient(135deg, #849778 0%, ${SAGE} 60%, ${SAGE_DARK} 100%)`,
                boxShadow: "0 10px 30px -8px rgba(122,139,111,0.45)",
              }}
            >
              <ArrowLeft
                className="w-4 h-4 transition-transform group-hover:-translate-x-1"
                strokeWidth={2.5}
              />
              Back to Home
            </a>
            <Link
              href="/sign-up"
              data-testid="link-sign-up"
              className="inline-flex items-center justify-center px-7 py-4 rounded-[18px] font-bold text-[17px] transition-all hover:scale-[1.02] active:scale-[0.98] bg-white border"
              style={{
                color: INK,
                borderColor: BORDER,
                boxShadow: "0 10px 30px -12px rgba(0,0,0,0.15)",
              }}
            >
              Sign Up
            </Link>
          </div>
        </div>
      </main>

      <SalesFooter />
      {/* Same Digital Receptionist bubble as the root sales page — also the
          header CTA's target when the hero button opens the pricing chat. */}
      <SalesReceptionist />
    </div>
  );
}
